import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HeartHandshake, Compass, Gem, Cpu, Coins, Mic,
  ShieldCheck, Code2, Languages, BookOpenCheck, Monitor, Apple,
  Brain, Music, Globe,
} from 'lucide-react';

const skillsConfig = [
  { id: 'empathy', icon: HeartHandshake, accent: 'from-rose-500 to-rose-600', tag: 'bg-rose-50 text-rose-700' },
  { id: 'methodology', icon: Compass, accent: 'from-sky-500 to-sky-600', tag: 'bg-sky-50 text-sky-700' },
  { id: 'coreValues', icon: Gem, accent: 'from-amber-500 to-amber-600', tag: 'bg-amber-50 text-amber-700' },
  { id: 'philosophy', icon: Brain, accent: 'from-stone-600 to-stone-700', tag: 'bg-stone-50 text-stone-700' },
  { id: 'music', icon: Music, accent: 'from-pink-500 to-pink-600', tag: 'bg-pink-50 text-pink-700' },
  { id: 'ai', icon: Cpu, accent: 'from-violet-500 to-violet-600', tag: 'bg-violet-50 text-violet-700' },
  { id: 'finance', icon: Coins, accent: 'from-emerald-500 to-emerald-600', tag: 'bg-emerald-50 text-emerald-700' },
  { id: 'podcasting', icon: Mic, accent: 'from-fuchsia-500 to-fuchsia-600', tag: 'bg-fuchsia-50 text-fuchsia-700' },
  { id: 'pix', icon: ShieldCheck, accent: 'from-blue-600 to-blue-700', tag: 'bg-blue-50 text-blue-700', hasBadge: true },
  { id: 'python', icon: Code2, accent: 'from-indigo-500 to-indigo-600', tag: 'bg-indigo-50 text-indigo-700', hasBadge: true },
  { id: 'tcf', icon: Languages, accent: 'from-red-500 to-red-600', tag: 'bg-red-50 text-red-700', hasBadge: true },
  { id: 'fle', icon: Globe, accent: 'from-teal-500 to-teal-600', tag: 'bg-teal-50 text-teal-700', hasBadge: true },
  { id: 'hsk', icon: BookOpenCheck, accent: 'from-orange-500 to-orange-600', tag: 'bg-orange-50 text-orange-700', hasBadge: true },
  { id: 'microsoft', icon: Monitor, accent: 'from-cyan-500 to-cyan-600', tag: 'bg-cyan-50 text-cyan-700', hasBadge: true },
  { id: 'apple', icon: Apple, accent: 'from-slate-600 to-slate-800', tag: 'bg-slate-100 text-slate-700', hasBadge: true },
];

export const Skills = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="skills" data-testid="skills-section" ref={ref} className="py-16 lg:py-20 bg-white relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 -left-32 w-96 h-96 bg-amber-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-20 -right-32 w-96 h-96 bg-sky-100/40 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-amber-600 font-inter text-sm font-semibold tracking-wider uppercase">
            {t('skills.tag')}
          </span>
          <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-4 mb-6">
            {t('skills.title1')} <span className="italic">{t('skills.title1Italic')}</span> :<br />
            {t('skills.title2')} <span className="italic">{t('skills.title2Italic')}</span> {t('skills.title3')}
          </h2>
          <div className="section-divider mb-6" />
          <p className="font-inter text-slate-600 text-lg max-w-2xl mx-auto">
            {t('skills.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 lg:gap-6">
          {skillsConfig.map((skill, index) => (
            <motion.article
              key={skill.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.05 * index }}
              data-testid={`skill-card-${skill.id}`}
              className="group relative bg-white rounded-2xl border border-slate-200 p-6 card-hover overflow-hidden"
            >
              <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${skill.accent}`} />

              <div className="flex items-start justify-between mb-5">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${skill.accent} flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform duration-300`}>
                  <skill.icon className="w-6 h-6 text-white" />
                </div>
                {skill.hasBadge && (
                  <span className="text-[10px] font-bold tracking-wider uppercase bg-slate-900 text-amber-400 px-2.5 py-1 rounded-full">
                    {t(`skills.items.${skill.id}.badge`)}
                  </span>
                )}
              </div>

              <h3 className="font-playfair text-xl font-bold text-slate-900 leading-tight mb-1">
                {t(`skills.items.${skill.id}.title`)}
              </h3>
              <p className="font-inter text-sm text-slate-500 mb-3">
                {t(`skills.items.${skill.id}.subtitle`)}
              </p>
              <p className="font-inter text-slate-600 text-sm leading-relaxed mb-5">
                {t(`skills.items.${skill.id}.description`)}
              </p>

              <span className={`inline-block text-xs font-medium px-3 py-1 rounded-full ${skill.tag}`}>
                {t(`skills.items.${skill.id}.category`)}
              </span>
            </motion.article>
          ))}

          {/* Logo card */}
          <motion.article
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.05 * (skillsConfig.length + 1) }}
            data-testid="skill-card-logo"
            className="group relative bg-white rounded-2xl p-6 border border-slate-200 flex flex-col items-center justify-center text-center hover:shadow-xl transition-shadow overflow-hidden"
          >
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-red-500 to-amber-400" />
            <img
              src="https://customer-assets.emergentagent.com/job_2af900fb-a4c0-4108-9990-fef980765d9c/artifacts/gr6pak3y_LAFAYETTE_LOGO-04.jpg"
              alt="Lafayette International School logo"
              className="w-32 h-32 object-contain group-hover:scale-105 transition-transform duration-500"
            />
            <p className="font-playfair text-lg font-bold text-slate-900 mt-4 leading-tight">
              Lafayette
            </p>
            <p className="font-inter text-xs text-slate-500 tracking-widest uppercase mt-1">
              International School
            </p>
          </motion.article>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          data-testid="skills-highlight-band"
          className="mt-16 bg-slate-900 rounded-3xl p-8 lg:p-12"
        >
          <div className="text-center mb-10 pb-10 border-b border-slate-800">
            <span className="inline-block text-[11px] font-bold tracking-wider uppercase bg-amber-500/20 text-amber-300 px-3 py-1 rounded-full mb-4">
              {t('skills.info.badge')}
            </span>
            <h3 className="font-playfair text-2xl lg:text-3xl font-bold text-white mb-2">
              {t('skills.info.title')}
            </h3>
            <p className="font-caveat text-xl text-amber-400 mb-4">
              {t('skills.info.subtitle')}
            </p>
            <p className="font-inter text-slate-300 text-base max-w-2xl mx-auto leading-relaxed">
              {t('skills.info.description')}
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div data-testid="stat-certifications">
              <div className="font-playfair text-4xl lg:text-5xl font-bold text-amber-400 mb-2">8</div>
              <p className="font-inter text-slate-300 text-sm">{t('skills.stats.certifications')}</p>
            </div>
            <div data-testid="stat-languages">
              <div className="font-playfair text-4xl lg:text-5xl font-bold text-amber-400 mb-2">4</div>
              <p className="font-inter text-slate-300 text-sm">{t('skills.stats.languages')}</p>
            </div>
            <div data-testid="stat-skills">
              <div className="font-playfair text-4xl lg:text-5xl font-bold text-amber-400 mb-2">15</div>
              <p className="font-inter text-slate-300 text-sm">{t('skills.stats.skills')}</p>
            </div>
            <div data-testid="stat-future">
              <div className="font-playfair text-4xl lg:text-5xl font-bold text-amber-400 mb-2">100%</div>
              <p className="font-inter text-slate-300 text-sm">{t('skills.stats.future')}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
